import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import api from '../services/api';
import type { AccountBudgetProgressDto, TaskBudgetBreakdownRow } from '../types/accountBudget';
import { formatAccountMetricValue } from '../utils/accountMetricFormat';
import { metricFromBudgetType } from '../types/accountTrend';

interface TaskBudgetSliceCardProps {
  accountId: string;
  taskId: string;
  canEdit: boolean;
  economyVisible: boolean;
  onTargetSaved?: () => void;
}

export default function TaskBudgetSliceCard({
  accountId,
  taskId,
  canEdit,
  economyVisible,
  onTargetSaved
}: TaskBudgetSliceCardProps) {
  const [progress, setProgress] = useState<AccountBudgetProgressDto | null>(null);
  const [row, setRow] = useState<TaskBudgetBreakdownRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDraft, setTargetDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!economyVisible) {
      setProgress(null);
      setRow(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.getAccountBudgetProgress(accountId);
      if (res.success && res.data.hasBudget) {
        const p = res.data.progress as AccountBudgetProgressDto;
        setProgress(p);
        const match = p.taskBreakdown?.find((t) => t.taskId === taskId) ?? null;
        setRow(match);
      } else {
        setProgress(null);
        setRow(null);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setProgress(null);
        setRow(null);
        return;
      }
      setError(message ?? 'Failed to load budget');
    } finally {
      setLoading(false);
    }
  }, [accountId, taskId, economyVisible]);

  useEffect(() => {
    load();
  }, [load]);

  if (!economyVisible) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (progress?.targetMode !== 'PER_TASK' || !progress?.period.active || !row) {
    return null;
  }

  const metric = metricFromBudgetType(progress.type);
  const currencyCode = progress.currencyCode ?? '';
  const formatVal = (v: number) => formatAccountMetricValue(v, metric, currencyCode);

  const cancelEdit = () => {
    setEditingTarget(false);
    setTargetDraft('');
  };

  const startEdit = () => {
    setTargetDraft(row.target != null ? String(row.target) : '');
    setEditingTarget(true);
  };

  const saveTarget = async () => {
    try {
      setSaving(true);
      await api.upsertAccountTaskTargets(accountId, [
        { taskId, targetAmount: targetDraft === '' ? null : targetDraft }
      ]);
      cancelEdit();
      await load();
      onTargetSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Budget · {progress.period.label}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!row.inScope ? (
          <Typography variant="body2" color="text.secondary">
            This task is outside the current budget scope.
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Registered (budget period): {formatVal(row.registered)}
            </Typography>
            {canEdit ? (
              editingTarget ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <TextField
                    size="small"
                    label="Target"
                    value={targetDraft}
                    onChange={(e) => setTargetDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void saveTarget();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEdit();
                      }
                    }}
                    autoFocus
                    sx={{ width: 120 }}
                  />
                  <Button size="small" onClick={saveTarget} disabled={saving}>
                    Save
                  </Button>
                  <Button size="small" onClick={cancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {row.target != null ? (
                    <Typography variant="body2">Target: {formatVal(row.target)}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Target: Not set
                    </Typography>
                  )}
                  <IconButton size="small" aria-label="Edit target" onClick={startEdit}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            ) : row.target != null ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Target: {formatVal(row.target)}
              </Typography>
            ) : null}
            {row.target != null && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, row.percent ?? 0)}
                  sx={{ mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {(row.percent ?? 0).toFixed(0)}% of task target
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
